import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { TCPADisclosure } from '../components/TCPADisclosure';
import { BookOpenIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon, GraduationCapIcon, UsersIcon, ClockIcon, DollarSignIcon, ShieldCheckIcon, HeartIcon, BrainIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
const hcuEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'No High School Diploma',
  value: '2'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}];
const hcuStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
const degreeLevels = [{
  label: "Bachelor's",
  value: 'bachelors'
}, {
  label: "Master's",
  value: 'masters'
}, {
  label: 'Doctoral',
  value: 'doctoral'
}];
interface HcuProgram {
  shortTitle: string;
  longTitle: string;
  degreeLevel: string;
  areaOfStudy: string;
  mProgram: string;
}
const hcuPrograms: HcuProgram[] = [
// Bachelor's - Arts
{
  shortTitle: 'B.A. - Biblical Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Biblical Studies - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67002'
}, {
  shortTitle: 'B.A. - Criminal Justice',
  longTitle: 'Bachelor of Arts (B.A.) in Criminal Justice - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67003'
}, {
  shortTitle: 'B.A. - Interdisciplinary Studies - Business Track',
  longTitle: 'Bachelor of Arts (B.A.) in Interdisciplinary Studies - Business Track - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67004'
}, {
  shortTitle: 'B.A. - Interdisciplinary Studies - General Math and Science Track',
  longTitle: 'Bachelor of Arts (B.A.) in Interdisciplinary Studies - General Math and Science Track - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67034'
}, {
  shortTitle: 'B.A. - Interdisciplinary Studies - Humanities Track',
  longTitle: 'Bachelor of Arts (B.A.) in Interdisciplinary Studies - Humanities Track - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67033'
}, {
  shortTitle: 'B.A. - Interdisciplinary Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Interdisciplinary Studies - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67051'
}, {
  shortTitle: 'B.A. - Psychology - Christian Counseling',
  longTitle: 'Bachelor of Arts (B.A.) in Psychology - Christian Counseling - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67006'
}, {
  shortTitle: 'B.A. - Psychology - Marriage and Family Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Psychology - Marriage and Family Studies - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67007'
}, {
  shortTitle: 'B.A. - Psychology',
  longTitle: 'Bachelor of Arts (B.A.) in Psychology - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67008'
}, {
  shortTitle: 'B.A. - Psychology to Master of Arts in Psychology (5 Year Accelerated)',
  longTitle: 'Bachelor of Arts (B.A.) in Psychology to Master of Arts in Psychology (5 Year Accelerated) - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67048'
}, {
  shortTitle: 'B.A. - Theological Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Theological Studies - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67009'
},
// Bachelor's - Business Administration
{
  shortTitle: 'B.B.A. - Business Administration',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Business Administration - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67005'
}, {
  shortTitle: 'B.B.A. - Business Data Analytics',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Business Data Analytics - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67052'
}, {
  shortTitle: 'B.B.A. - Human Resource Development and Change',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Human Resource Development and Change - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67053'
}, {
  shortTitle: 'B.B.A. - International Business',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in International Business - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67031'
}, {
  shortTitle: 'B.B.A. - Management Information Systems',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Management Information Systems - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67054'
}, {
  shortTitle: 'B.B.A. - Management',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Management - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67001'
}, {
  shortTitle: 'B.B.A. - Marketing',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Marketing - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67032'
},
// Master's - Arts
{
  shortTitle: 'M.A. - Apologetics',
  longTitle: 'Master of Arts (M.A.) in Apologetics - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67011'
}, {
  shortTitle: 'M.A. - Christian Leadership - Practical Theology Track',
  longTitle: 'Master of Arts (M.A.) in Christian Leadership - Practical Theology Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67014'
}, {
  shortTitle: 'M.A. - Clinical Mental Health Counseling',
  longTitle: 'Master of Arts (M.A.) in Clinical Mental Health Counseling - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67015'
}, {
  shortTitle: 'M.A. - Human Services - Marriage and Family Track',
  longTitle: 'Master of Arts (M.A.) in Human Services - Marriage and Family Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67016'
}, {
  shortTitle: 'M.A. - Human Services - Professional Life Coaching Track',
  longTitle: 'Master of Arts (M.A.) in Human Services - Professional Life Coaching Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67040'
}, {
  shortTitle: 'M.A. - Marriage and Family Therapy',
  longTitle: 'Master of Arts (M.A.) in Marriage and Family Therapy - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67018'
}, {
  shortTitle: 'M.A. - Psychology - Applied Project Track',
  longTitle: 'Master of Arts (M.A.) in Psychology - Applied Project Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67041'
}, {
  shortTitle: 'M.A. - Psychology - Teaching Track',
  longTitle: 'Master of Arts (M.A.) in Psychology - Teaching Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67044'
}, {
  shortTitle: 'M.A. - Psychology - Thesis Track',
  longTitle: 'Master of Arts (M.A.) in Psychology - Thesis Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67042'
}, {
  shortTitle: 'M.A. - Theological Studies - Theological Studies Track',
  longTitle: 'Master of Arts (M.A.) in Theological Studies - Theological Studies Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67019'
},
// Master's - Business Administration
{
  shortTitle: 'M.B.A. - Professional MBA in Accounting and Finance',
  longTitle: 'Master of Business Administration (M.B.A.) in Professional MBA in Accounting and Finance - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '67039'
}, {
  shortTitle: 'M.B.A. - Professional MBA in Business Data Analytics',
  longTitle: 'Master of Business Administration (M.B.A.) in Professional MBA in Business Data Analytics - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '67013'
}, {
  shortTitle: 'M.B.A. - Professional MBA in Management',
  longTitle: 'Master of Business Administration (M.B.A.) in Professional MBA in Management - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '67010'
},
// Master's - Divinity
{
  shortTitle: 'M.Div. - Biblical Languages Track',
  longTitle: 'Master of Divinity (M.Div.) in Biblical Languages Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'divinity',
  mProgram: '67020'
}, {
  shortTitle: 'M.Div. - English Language Track',
  longTitle: 'Master of Divinity (M.Div.) in English Language Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'divinity',
  mProgram: '67021'
},
// Master's - Education
{
  shortTitle: 'M.Ed. - Curriculum and Instruction',
  longTitle: 'Master of Education (M.Ed.) in Curriculum and Instruction - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '67022'
}, {
  shortTitle: 'M.Ed. - Educational Administration',
  longTitle: 'Master of Education (M.Ed.) in Educational Administration - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '67023'
}, {
  shortTitle: 'M.Ed. - Professional School Counseling',
  longTitle: 'Master of Education (M.Ed.) in Professional School Counseling - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '67045'
},
// Master's - Health Administration
{
  shortTitle: 'M.H.A. - Professional Science Masters in Health Professions',
  longTitle: 'Master of Health Administration (M.H.A.) in Professional Science Masters in Health Professions - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'health',
  mProgram: '67017'
},
// Master's - Science
{
  shortTitle: 'M.S. - Artificial Intelligence',
  longTitle: 'Master of Science (M.S.) in Artificial Intelligence - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67028'
}, {
  shortTitle: 'M.S.C.J. - Criminal Justice',
  longTitle: 'Master of Science (M.S.C.J.) in Criminal Justice - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67012'
}, {
  shortTitle: 'M.S. - Computer and Information Sciences',
  longTitle: 'Master of Science (M.S.) in Computer and Information Sciences - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67036'
}, {
  shortTitle: 'M.S. - Cybersecurity',
  longTitle: 'Master of Science (M.S.) in Cybersecurity - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67043'
}, {
  shortTitle: 'M.S. - Data Science',
  longTitle: 'Master of Science (M.S.) in Data Science - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67037'
}, {
  shortTitle: 'M.S. - Human Resource Management in People Analytics',
  longTitle: 'Master of Science (M.S.) in Human Resource Management in People Analytics - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67026'
}, {
  shortTitle: 'M.S. - Kinesiology - Sports Management',
  longTitle: 'Master of Science (M.S.) in Kinesiology - Sports Management - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67027'
}, {
  shortTitle: 'M.S. - Leadership',
  longTitle: 'Master of Science (M.S.) in Leadership - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67035'
}, {
  shortTitle: 'M.S. - Learning, Technology, and Design',
  longTitle: 'Master of Science (M.S.) in Learning, Technology, and Design - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67025'
},
// Master's - Nursing
{
  shortTitle: 'M.S.N. - Family Nurse Practitioner',
  longTitle: 'Master of Science in Nursing (M.S.N.) in Family Nurse Practitioner - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'nursing',
  mProgram: '67038'
}, {
  shortTitle: 'M.S.N. - Nurse Education',
  longTitle: 'Master of Science in Nursing (M.S.N.) in Nurse Education - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'nursing',
  mProgram: '67046'
}, {
  shortTitle: 'M.S.N. - Nurse Leadership',
  longTitle: 'Master of Science in Nursing (M.S.N.) in Nurse Leadership - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'nursing',
  mProgram: '67047'
}, {
  shortTitle: 'M.S.N. - Pediatric Nurse Practitioner',
  longTitle: 'Master of Science in Nursing (M.S.N.) in Pediatric Nurse Practitioner - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'nursing',
  mProgram: '67049'
}, {
  shortTitle: 'M.S.N. - RN to MSN',
  longTitle: 'Master of Science in Nursing (M.S.N.) in RN to MSN - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'nursing',
  mProgram: '67050'
},
// Doctoral
{
  shortTitle: 'Ed.D. - Executive Educational Leadership',
  longTitle: 'Doctor of Education (Ed.D.) in Executive Educational Leadership - Online',
  degreeLevel: 'doctoral',
  areaOfStudy: 'education',
  mProgram: '67029'
}, {
  shortTitle: 'Ed.D. - Mental Health and Human Services',
  longTitle: 'Doctor of Education (Ed.D.) in Mental Health and Human Services - Online',
  degreeLevel: 'doctoral',
  areaOfStudy: 'education',
  mProgram: '67030'
}, {
  shortTitle: 'Ed.S. - Educational Leadership',
  longTitle: 'Educational Specialist (Ed.S.) in Educational Leadership - Online',
  degreeLevel: 'doctoral',
  areaOfStudy: 'education',
  mProgram: '67024'
}];
export function HCULandingPage() {
  usePageMeta('Houston Christian University | Online Degree Programs', 'Explore flexible online degree programs at Houston Christian University. Faith-based education combining reason, liberal arts, and technical training for working adults.', {
    ogImage: 'https://cdn.magicpatterns.com/uploads/7rHHFpReLC371VmGSsY1sD/67119a3a74d41da2543935f5_Houston_Christian_University_nav.png',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [selectedDegreeLevel, setSelectedDegreeLevel] = useState('');
  const [selectedAreaOfStudy, setSelectedAreaOfStudy] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const areaLabels: Record<string, string> = {
    arts: 'Arts',
    business: 'Business Administration',
    divinity: 'Divinity',
    education: 'Education',
    health: 'Health Administration',
    science: 'Science',
    nursing: 'Nursing'
  };
  const allAreaSlugs = Object.keys(areaLabels);
  const getAvailableAreas = () => {
    if (!selectedDegreeLevel || selectedDegreeLevel === 'undecided') {
      return allAreaSlugs;
    }
    const areasWithPrograms = new Set<string>();
    hcuPrograms.forEach(prog => {
      if (prog.degreeLevel === selectedDegreeLevel) {
        areasWithPrograms.add(prog.areaOfStudy);
      }
    });
    return allAreaSlugs.filter(slug => areasWithPrograms.has(slug));
  };
  const getAvailablePrograms = () => {
    if (!selectedAreaOfStudy) return [];
    return hcuPrograms.filter(prog => {
      const areaMatch = prog.areaOfStudy === selectedAreaOfStudy;
      const degreeMatch = !selectedDegreeLevel || prog.degreeLevel === selectedDegreeLevel;
      return areaMatch && degreeMatch;
    });
  };
  const handleDegreeLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLevel = e.target.value;
    setSelectedDegreeLevel(newLevel);
    setSelectedAreaOfStudy('');
    setSelectedProgram('');
  };
  const handleAreaOfStudyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newArea = e.target.value;
    setSelectedAreaOfStudy(newArea);
    setSelectedProgram('');
  };
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProgram = e.target.value;
    setSelectedProgram(newProgram);
    if (newProgram && formStep === 0) {
      setFormStep(1);
    }
  };
  const currentProgram = hcuPrograms.find(p => p.mProgram === selectedProgram);
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          programTitle: currentProgram?.longTitle || '',
          degreeLevel: selectedDegreeLevel,
          areaOfStudy: selectedAreaOfStudy,
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/4496hy3/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'Houston Christian University',
            logo: 'https://cdn.magicpatterns.com/uploads/7rHHFpReLC371VmGSsY1sD/67119a3a74d41da2543935f5_Houston_Christian_University_nav.png',
            primaryColor: '#002F6C',
            accentColor: '#A6192E',
            backUrl: '/universities',
            programTitle: currentProgram?.longTitle || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#002F6C] focus:border-[#002F6C]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#002F6C] focus:border-[#002F6C]'}`;
  const availableAreas = getAvailableAreas();
  const availablePrograms = getAvailablePrograms();
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-1802" data-mp="gpqvyhpxj0fr">
          <div data-id="element-1803" data-mp="goz2bcfw7cs4">
            <label htmlFor="degree-level-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1804" data-mp="gmcq2zi2493k">
              Degree Level
            </label>
            <div className="relative" data-id="element-1805" data-mp="lo23umwnz0vc">
              <select id="degree-level-select" value={selectedDegreeLevel} onChange={handleDegreeLevelChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#002F6C] focus:border-[#002F6C] font-medium text-gray-800" data-id="element-1806" data-mp="ln0jtsxo741h">
                <option value="" data-id="element-1807" data-mp="29dge0sq5pm83">-- Select Degree Level --</option>
                {degreeLevels.map(level => <option key={level.value} value={level.value} data-id="element-1808" data-mp="1uplkxb88lfuh">
                    {level.label}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1809" data-mp="2ncg507156d7f">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1810" data-mp="2scass1pmemo9" />
              </div>
            </div>
          </div>

          <div data-id="element-1811" data-mp="2eb89fwq0fqh1">
            <label htmlFor="area-of-study-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1812" data-mp="2ebqar7rj4uvt">
              Area of Study
            </label>
            <div className="relative" data-id="element-1813" data-mp="12z7f82adn9tw">
              <select id="area-of-study-select" value={selectedAreaOfStudy} onChange={handleAreaOfStudyChange} disabled={!selectedDegreeLevel} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#002F6C] focus:border-[#002F6C] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-1814" data-mp="16z7tnmxh7l0y">
                <option value="" data-id="element-1815" data-mp="2658cak2t69yk">
                  {selectedDegreeLevel ? '-- Select Area of Study --' : 'Select degree level first'}
                </option>
                {availableAreas.map(slug => <option key={slug} value={slug} data-id="element-1816" data-mp="1w8lw48lfg72v">
                    {areaLabels[slug]}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1817" data-mp="krm298jflk6q">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1818" data-mp="gozb79ul43al" />
              </div>
            </div>
          </div>

          <div data-id="element-1819" data-mp="2seozuguah5ii">
            <label htmlFor="program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1820" data-mp="2sb13zb1dg70m">
              Program
            </label>
            <div className="relative" data-id="element-1821" data-mp="31bz95wnjqkks">
              <select id="program-select" value={selectedProgram} onChange={handleProgramChange} disabled={!selectedAreaOfStudy} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#002F6C] focus:border-[#002F6C] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-1822" data-mp="364sdijf3rqwc">
                <option value="" data-id="element-1823" data-mp="1pqsh3ifgujdu">
                  {!selectedAreaOfStudy ? 'Select area of study first' : '-- Select Program --'}
                </option>
                {availablePrograms.map(prog => <option key={prog.mProgram} value={prog.mProgram} data-id="element-1824" data-mp="2y0j8lhryaqdg">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1825" data-mp="3720jmlqi41va">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1826" data-mp="320b9d8b3rs89" />
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center pt-2" data-id="element-1827" data-mp="3gxaodh8aancr">
            Select all three options above to continue
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-1828" data-mp="3pqnnj0wxyl7a">
          <div data-id="element-1829" data-mp="3lq3ti67nzxpd">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1830" data-mp="3lplnihv7a549">
              Selected Program
            </label>
            <div className="relative" data-id="element-1831" data-mp="3url5bce1hd89">
              <select id="step1-program" value={selectedProgram} onChange={e => {
              setSelectedProgram(e.target.value);
            }} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-1832" data-mp="3uofiy4wsopg8">
                {availablePrograms.map(prog => <option key={prog.mProgram} value={prog.mProgram} data-id="element-1833" data-mp="3vland6oy7m4g">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-1834" data-mp="48zhgh38qmub">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1835" data-mp="3qmhke87afdij" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-1836" data-mp="3cmgbc5gvczrz" />

          <div data-id="element-1837" data-mp="3cp2qaq63ahk8">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1838" data-mp="3gojxpp52tos1">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-1839" data-mp="3s4q0rwypgb1p">
              <option value="" data-id="element-1840" data-mp="3e5ghupj472u1">-- Please Select --</option>
              {hcuEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-1841" data-mp="39dglt8660u2q">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-1842" data-mp="2zhdfdq4a699z">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-1843" data-mp="2uj20v231jalf">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1844" data-mp="2qfvza7qvi7la">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-1845" data-mp="19yf1p1vclk9b">
              <option value="" data-id="element-1846" data-mp="1jva1do3cxqp6">-- Please Select --</option>
              {hcuStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-1847" data-mp="m7rhbh48x25v">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-1848" data-mp="w5p563i70wxy">{errors.startDate}</p>}
          </div>

          <div data-id="element-1849" data-mp="110lag5cfxgt0">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1850" data-mp="3j4df1xt2itv">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-1851" data-mp="3ed66fb7dmbtc">
              <option value="" data-id="element-1852" data-mp="35cgeiil8l5kw">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-1853" data-mp="39cs0pzt8zdyh">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-1854" data-mp="30e6oemedcv65">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-1855" data-mp="2lq1yoygwfjdv">
            <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1856" data-mp="2ln615b8yqwpy">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1857" data-mp="2hpi2bx45gjss" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#002F6C] to-[#001A3D] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1858" data-mp="1y3pyye5nm6g0">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-1859" data-mp="2c1enpx2udxqc">
          <div data-id="element-1860" data-mp="2c0db3o66h36i">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1861" data-mp="2c0vcez7p67la">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-1862" data-mp="2lrq0m94i7h9d" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-1863" data-mp="265v67fli3472">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-1864" data-mp="2b5hk7r95kogx">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-1865" data-mp="2g42l7sw8bc6f" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-1866" data-mp="2p4h4kmpmuh7a">
                <div data-id="element-1867" data-mp="2p3xyc39chh93">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1868" data-mp="2p2ehaafv7269">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#002F6C] focus:border-[#002F6C] bg-gray-50" placeholder="City" data-id="element-1869" data-mp="n7qdhn3ekfv4" />
                </div>
                <div data-id="element-1870" data-mp="1659q10p2kgma">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1871" data-mp="116yiiwm8yi78">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#002F6C] focus:border-[#002F6C] bg-gray-50" placeholder="State" data-id="element-1872" data-mp="s731wc9x9uzt" />
                </div>
              </div>
              <div data-id="element-1873" data-mp="vn4in3nsi84w">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1874" data-mp="vnxjxp3zc5fn">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#002F6C] focus:border-[#002F6C]'}`} placeholder="123 Main St" data-id="element-1875" data-mp="1netycu5oui51" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-1876" data-mp="3vkmf4yx4q0hm">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-1877" data-mp="3rmopjuzg5cdp">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1878" data-mp="9do4tym42qy5">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1879" data-mp="37x4hwnv3ia79" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#002F6C] to-[#001A3D] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1880" data-mp="3mliqlff70svq">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-1881" data-mp="t278s9yd9v4t">
          <div className="grid grid-cols-2 gap-3" data-id="element-1882" data-mp="t0w1i05pq58y">
            <div data-id="element-1883" data-mp="y0ihghcxdzlg">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1884" data-mp="xzyzx1r6td7y">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-1885" data-mp="16vy7enak7ulr" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-1886" data-mp="o4ov669emrqh">{errors.firstName}</p>}
            </div>
            <div data-id="element-1887" data-mp="1uiwl5d7aoofz">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1888" data-mp="1ujg5t1obxqxp">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-1889" data-mp="29ok8e4j092px" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-1890" data-mp="1qx9j1rsxgy05">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-1891" data-mp="1lvk8ed9mamcl">
            <div data-id="element-1892" data-mp="1gxindmsmscyh">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1893" data-mp="1gz3ibdiuxxwv">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-1894" data-mp="3barxngt042hs" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-1895" data-mp="3gtfzzc9s1s0g">{errors.email}</p>}
            </div>
            <div data-id="element-1896" data-mp="3ls9ftbcjdv3s">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1897" data-mp="3lrpy9vqst8qa">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-1898" data-mp="3bvelz5r7kivu" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-1899" data-mp="111s8vxhg2tpk">{errors.phone}</p>}
            </div>
          </div>

          <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#002F6C] focus:ring-[#002F6C] cursor-pointer flex-shrink-0" data-id="element-1900" data-mp="1b3urcbcrumin">
            By submitting this form, I confirm that the information provided on
            this form is accurate and complete. I agree that Houston Christian
            University and its affiliates may call and/or text me about its
            offerings using automated technology. I understand consent is not a
            condition of purchasing services.
          </TCPADisclosure>

          <div className="flex gap-3 pt-2" data-id="element-1901" data-mp="8j3yuvibd8ba">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1902" data-mp="8jvx6e3apk59">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1903" data-mp="2ayfpl093qgxz" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#A6192E] to-[#7A1222] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1904" data-mp="2oxou32ny3mm4">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1905" data-mp="38mplcxiao8gn" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-1906" data-mp="3cmrmn5k4azve">
      {/* Header */}
      <header className="bg-white py-2 sm:py-3 px-4 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-1907" data-mp="2vupmkek348">
        <img src="https://cdn.magicpatterns.com/uploads/7rHHFpReLC371VmGSsY1sD/67119a3a74d41da2543935f5_Houston_Christian_University_nav.png" alt="Houston Christian University Logo" className="h-10 sm:h-16 w-auto object-contain" data-id="element-1908" data-mp="51qbyk2q1jm8" />
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#002F6C] to-[#001A3D] relative overflow-hidden" data-id="element-1909" data-mp="3dha23a83ilx2">
        {/* Explicit grid placement rather than a duplicated list: on mobile the
            form follows the headline so it lands within reach, and the selling
            points move below it. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-24 grid gap-8 lg:grid-cols-2 lg:gap-x-16 lg:gap-y-6 relative z-10" data-id="element-1910" data-mp="38i5vmrck32af">
          {/* Headline */}
          <div className="text-white max-w-xl mx-auto lg:mx-0 text-center lg:text-left lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-1911" data-mp="2ulaaw1mxzmej">
            <p className="text-white font-medium mb-3 text-lg tracking-wide" data-id="element-1912" data-mp="152295vciz0at">
              Houston Christian University
            </p>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.1] text-white tracking-tight" data-id="element-1913" data-mp="109isuqmgdr2b">
              {currentProgram ? currentProgram.longTitle : <>Online Degrees at Houston Christian University</>}
            </h1>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] lg:justify-self-end bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-6 sm:p-10 relative z-10 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center" data-id="element-1914" data-mp="1j11uou4qqz2r">
            <form onSubmit={e => e.preventDefault()} id="hcu-lead-form" data-id="element-1915" data-mp="1a382nve0erle">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" value="" data-id="element-1916" data-mp="3urdyia1o9o8m" />
              <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-1917" data-mp="2a78vl6gn6c4f" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#002F6C] mb-4" data-id="element-1918" data-mp="2j8715t6qarp4">
                Take the next step in your education.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-1919" data-mp="2syrvv5ja7c2r">
                <div className="bg-gradient-to-r from-[#A6192E] to-[#7A1222] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-1920" data-mp="2xxlc35pydh6m"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-1921" data-mp="18khvsyjbrq8k">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-1922" data-mp="18lsyev11ary3">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>

          {/* Selling points. Placed after the form in the markup so mobile gets
              them below it; the grid puts them back under the headline on
              desktop. */}
          <ul className="space-y-2.5 max-w-lg mx-auto lg:mx-0 lg:col-start-1 lg:row-start-2 lg:self-start" data-id="element-1923" data-mp="8c9rhzovveqg">
            {['Integrates faith and reason in academics', 'Courses available at any time of the day', '60-year academic tradition focused on quality', 'Some of the lowest tuition available today', 'SACSCOC Accredited'].map(item => <li key={item} className="flex items-start text-base lg:text-lg text-white" data-id="element-1924" data-mp="1r9o8cvwta5fl">
                <CheckCircle2Icon className="w-5 h-5 mt-0.5 text-white/80 mr-3 flex-shrink-0" data-id="element-1925" data-mp="1djaz8q86okoo" />
                {item}
              </li>)}
          </ul>
        </div>
      </section>

      {/* Reasons to Choose HCU */}
      <section className="bg-white py-16 border-b border-gray-200" data-id="element-1926" data-mp="25731obq6qxcu">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1927" data-mp="2a7gz6zu5uowm">
          <div className="mb-10" data-id="element-1928" data-mp="1w90t9b44h2fj">
            <div className="w-12 h-1 bg-[#002F6C] mb-4" data-id="element-1929" data-mp="1w7pnj5r49kls"></div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#002F6C]" data-id="element-1930" data-mp="216ssyrb0dsua">
              Reasons to Choose HCU
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1931" data-mp="krr9sokzuae8">
            <div className="border-r-0 md:border-r border-gray-200 pr-0 md:pr-8" data-id="element-1932" data-mp="pt6wel2tkoyq">
              <div className="w-14 h-14 bg-[#002F6C]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-1933" data-mp="2rhul7m4vdwdc">
                <HeartIcon className="w-7 h-7 text-[#002F6C]" data-id="element-1934" data-mp="2mhyjxunk4j1h" />
              </div>
              <h3 className="text-lg font-bold text-[#002F6C] mb-2" data-id="element-1935" data-mp="2hr9ok48rb1x2">
                Faith-Based Education
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1936" data-mp="2dovaryjy2scn">
                Integrate Christian values with academic excellence in every
                program.
              </p>
            </div>
            <div className="border-r-0 md:border-r border-gray-200 pr-0 md:pr-8" data-id="element-1937" data-mp="23qyz95n3hve0">
              <div className="w-14 h-14 bg-[#002F6C]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-1938" data-mp="1yrvuznexvt73">
                <BrainIcon className="w-7 h-7 text-[#002F6C]" data-id="element-1939" data-mp="1us3rvq8bef94" />
              </div>
              <h3 className="text-lg font-bold text-[#002F6C] mb-2" data-id="element-1940" data-mp="3t76pkqtvdcan">
                Liberal Arts Core
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1941" data-mp="3p6eia34k5hh0">
                Develop critical thinking through faith, reason, and liberal
                arts curriculum.
              </p>
            </div>
            <div data-id="element-1942" data-mp="1fuub6k2hm9yi">
              <div className="w-14 h-14 bg-[#002F6C]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-1943" data-mp="1fuaoyr5t0zeo">
                <ClockIcon className="w-7 h-7 text-[#002F6C]" data-id="element-1944" data-mp="1kphqbsblps0f" />
              </div>
              <h3 className="text-lg font-bold text-[#002F6C] mb-2" data-id="element-1945" data-mp="1onxmomtkocqi">
                Flexible Online Learning
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1946" data-mp="wxj8ephncyeb">
                Study on your schedule with fully online degree programs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose HCU */}
      <section className="py-16 bg-gray-50" data-id="element-1947" data-mp="15z0s0hu9ji7r">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1948" data-mp="2cebdka0f85ji">
          <div className="flex flex-col lg:flex-row items-start gap-12" data-id="element-1949" data-mp="2ce1n6lbgrwda">
            <div className="flex-1" data-id="element-1950" data-mp="2hcctim2jvoqr">
              <div className="w-12 h-1 bg-[#002F6C] mb-4" data-id="element-1951" data-mp="2hfh7gb5thyum"></div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#002F6C] mb-8" data-id="element-1952" data-mp="1t1m05gw0c5x4">
                Why Choose <span className="text-[#002F6C]" data-id="element-1953" data-mp="1o2slhl4zi8vd">HCU</span>
              </h2>
              <ul className="space-y-4" data-id="element-1954" data-mp="21tnztcy95i6c">
                <li className="flex items-start" data-id="element-1955" data-mp="21updlp6nkirr">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-1956" data-mp="1x1w5qsfw0ubg" />
                  <span className="text-gray-700" data-id="element-1957" data-mp="2bqsnjh7qxlp9">
                    <strong data-id="element-1958" data-mp="2bph8umcritjd">Faith-integrated curriculum</strong> combining
                    Christian teachings with academic rigor
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1959" data-mp="2kqp6d27gfqce">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-1960" data-mp="2gqez7m7xetvy" />
                  <span className="text-gray-700" data-id="element-1961" data-mp="qdzcull00j5j">
                    <strong data-id="element-1962" data-mp="qeqwqz58gsfz">Liberal arts foundation</strong> that develops
                    critical thinking and ethical leadership
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1963" data-mp="c873vcpfryos">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-1964" data-mp="cajsg8pp97at" />
                  <span className="text-gray-700" data-id="element-1965" data-mp="gc563wa25pm2">
                    <strong data-id="element-1966" data-mp="2hkuw1c866f2">Fully online programs</strong> designed for working
                    adults and busy schedules
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1967" data-mp="3oq3yknxtmsut">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-1968" data-mp="3ooaqr5bh22l8" />
                  <span className="text-gray-700" data-id="element-1969" data-mp="3tqi684vig0sb">
                    <strong data-id="element-1970" data-mp="3fpfjdpwoymgc">Expert faculty</strong> with real-world experience
                    and commitment to student success
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1971" data-mp="3kq3iyzyzkbm6">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-1972" data-mp="1dyud6kjmases" />
                  <span className="text-gray-700" data-id="element-1973" data-mp="1ivujmwkdpx6f">
                    <strong data-id="element-1974" data-mp="1iujcqnvn09b3">Career-focused education</strong> preparing you for
                    leadership in your field and community
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1975" data-mp="2plimm9w2ybqp">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-1976" data-mp="2umg0wbdyylmx" />
                  <span className="text-gray-700" data-id="element-1977" data-mp="2zdoap8j7o90m">
                    <strong data-id="element-1978" data-mp="2zfiz4wtoyj9g">Supportive Christian community</strong> of students,
                    faculty, and staff
                  </span>
                </li>
              </ul>
            </div>
            <div className="lg:w-[400px] flex-shrink-0" data-id="element-1979" data-mp="38g7crqf6ahlt">
              <img src="https://cdn.magicpatterns.com/uploads/jUSjuibhb7LUe6o3g91xYz/ChatGPT_Image_Jul_1,_2026,_01_59_51_AM.png" alt="Student studying at Houston Christian University" className="w-full h-[400px] object-cover rounded-2xl shadow-lg" data-id="element-1980" data-mp="3de8xsgw5sqzx" />
            </div>
          </div>
        </div>
      </section>

      {/* Accreditation Section */}
      <section className="py-16 bg-white" data-id="element-1981" data-mp="53y7fp65ax23">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1982" data-mp="cg793by50i6">
          <div className="w-12 h-1 bg-[#002F6C] mx-auto mb-6" data-id="element-1983" data-mp="b50cun7fcmu"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#002F6C] mb-6" data-id="element-1984" data-mp="3sha8sva1a1lb">
            Accreditation = Quality Education
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-1985" data-mp="3nf1ik6ojpdmw">
            Houston Christian University is accredited by the Southern
            Association of Colleges and Schools Commission on Colleges (SACSCOC)
            to award associate, baccalaureate, master's, and doctoral degrees.
          </p>
          <p className="text-gray-600 leading-relaxed" data-id="element-1986" data-mp="f35n4j0cr3sd">
            Our accreditation ensures that you receive a{' '}
            <strong data-id="element-1987" data-mp="2zra5v1z3eyl3">quality education</strong> that is recognized by employers
            and other institutions. Whether you're{' '}
            <strong data-id="element-1988" data-mp="2urp4kkwgae57">starting your college journey</strong> or{' '}
            <strong data-id="element-1989" data-mp="2w9vvu7oyj376">advancing your career</strong>, you can trust that your HCU
            degree represents excellence.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#002F6C] py-16" data-id="element-1990" data-mp="2n8xzmbkrfzz5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1991" data-mp="2n86410r5y28v">
          <div className="w-12 h-1 bg-white/40 mx-auto mb-6" data-id="element-1992" data-mp="2s798ok38e6gb"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-id="element-1993" data-mp="2dj4ofboj5mvf">
            Ready to Start Your Journey at{' '}
            <span className="text-white" data-id="element-1994" data-mp="2iaeegddzqs7u">HCU?</span>
          </h2>
          <div className="mt-8" data-id="element-1995" data-mp="2ibphgazm3vxw">
            <button onClick={scrollToForm} className="inline-block bg-white text-[#002F6C] font-bold py-3.5 px-12 rounded-full hover:shadow-lg hover:bg-gray-100 transition-all text-sm tracking-wide" data-id="element-1996" data-mp="24izqds5880tx">
              Get Started Now
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F5F5] text-gray-500 py-10 text-xs border-t border-gray-200" data-id="element-1997" data-mp="1zlg0tw32w1mo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3" data-id="element-1998" data-mp="1fzo06kvyw2dl">
          <p data-id="element-1999" data-mp="1azs3l6plnd82">
            Houston Christian University is accredited by the Southern
            Association of Colleges and Schools Commission on Colleges to award
            associate, baccalaureate, master's, and doctoral degrees.
          </p>
          <p data-id="element-2000" data-mp="1ywfnr6ijl3ng">
            For more information about HCU's online programs, visit{' '}
            <a href="https://hc.edu/online-learning/" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#002F6C]" data-id="element-2001" data-mp="1yu32okhhmd66">
              hc.edu/online-learning
            </a>
            .
          </p>
          <p className="pt-4 border-t border-gray-300 text-gray-400" data-id="element-2002" data-mp="23p8svvhxae02">
            © {new Date().getFullYear()} Houston Christian University. All
            rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}